import {Avatar, Card} from 'platform/components';
import {Box, HStack, Icon, Show, Text, ThemeIconKey, VStack} from 'platform/foundation';

import {isNotEmpty} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import type {SellerContact} from '../../../types/SellerContact';
import {CountryFlag} from '../../CountryFlag/CountryFlag';
import {ContactAction} from './ContactAction';
import {ContactRow} from './ContactRow';
import {TruncatedText} from './TruncatedText';

interface SellerCardProps extends TestIdProps {
  /** E.g. "Dealer" or "Pickup point". */
  title: string;
  name?: string | Nullish;
  /** Shown in the avatar instead of the initials of the name, e.g. for a pickup point. */
  avatarIcon?: ThemeIconKey;
  /** ISO 3166-1 alpha-3 code, drives the flag. */
  countryCode?: string | Nullish;
  address?: string | Nullish;
  mapUrl?: string | Nullish;
  phoneNumbers?: SellerContact[];
  email?: SellerContact | Nullish;
}

export function SellerCard(props: SellerCardProps) {
  const phoneNumbers = props.phoneNumbers ?? [];
  const hasContacts = isNotEmpty(phoneNumbers) || isNotNil(props.email);

  return (
    <Card data-testid={props['data-testid']}>
      <VStack spacing={4}>
        <Text size="xSmall" color="secondary">
          {props.title}
        </Text>
        <HStack spacing={3} align="center">
          <Avatar
            size="default"
            name={isNotNil(props.avatarIcon) ? undefined : props.name}
            icon={props.avatarIcon}
            data-testid={suffixTestId('avatar', props)}
          />
          <VStack spacing={1} grow={1} minWidth={0}>
            <Show when={isNotNilOrEmpty(props.name) ? props.name : null}>
              {(name) => (
                <Text alternative overflowWrap="anywhere" data-testid={suffixTestId('name', props)}>
                  {name}
                </Text>
              )}
            </Show>
            <Show when={isNotNilOrEmpty(props.address) ? props.address : null}>
              {(address) => (
                <HStack spacing={2} align="center">
                  <Show when={props.countryCode}>
                    {(countryCode) => <CountryFlag country={countryCode} size={4} />}
                  </Show>
                  <Box flex={1} minWidth={0}>
                    <TruncatedText
                      value={address}
                      color="secondary"
                      data-testid={suffixTestId('address', props)}
                    />
                  </Box>
                </HStack>
              )}
            </Show>
          </VStack>
          <Show when={props.mapUrl}>
            {(mapUrl) => (
              <ContactAction
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={i18n.t('entity.sourcing.vehiclePreview.actions.showOnMap')}
                title={i18n.t('entity.sourcing.vehiclePreview.actions.showOnMap')}
                data-testid={suffixTestId('map', props)}
              >
                <Icon value="communication/location_on" size={4} color="general.accent" />
              </ContactAction>
            )}
          </Show>
        </HStack>
        <Show when={hasContacts}>
          <VStack spacing={2}>
            {phoneNumbers.map((phone, index) => (
              <ContactRow
                key={phone.label}
                icon="communication/phone"
                actionLabel={i18n.t('general.labels.phone')}
                contact={phone}
                data-testid={suffixTestId(`phone-[${index}]`, props)}
              />
            ))}
            <Show when={props.email}>
              {(email) => (
                <ContactRow
                  icon="communication/email"
                  actionLabel={i18n.t('general.labels.email')}
                  contact={email}
                  data-testid={suffixTestId('email', props)}
                />
              )}
            </Show>
          </VStack>
        </Show>
      </VStack>
    </Card>
  );
}
